class RecommendationsAddToCart extends HTMLElement {
  constructor() {
    super();
    this.formId = 'recommendations-add-to-cart-form';

    // Check if we need to fetch recommendations or if they're already loaded
    if (this.innerHTML.trim() === '') {
      if (typeof window.initLazyScript === 'function') {
        window.initLazyScript(this, this.fetchRecommendations.bind(this), 500);
      } else {
        // Fallback if initLazyScript is not available
        this.fetchRecommendations();
      }
    } else {
      // Recommendations are already in the DOM
      this.init();
    }
  }

  async fetchRecommendations() {
    const { productId, url } = this.dataset;
    if (!productId || !url) {
      this.setErrorMsgState(theme.strings.missingProductId || 'Missing product ID or URL');
      this.dispatchEvent(new CustomEvent('on:recommendations:error', {
        bubbles: true,
        detail: { error: 'Missing product ID or URL' }
      }));
      return;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(response.status);

      const responseText = await response.text();

      // Try to parse as JSON first (for sectioned responses)
      try {
        const jsonResponse = JSON.parse(responseText);
        if (jsonResponse.sections) {
          const sectionHtml = Object.values(jsonResponse.sections)[0];
          const tmpl = document.createElement('template');
          tmpl.innerHTML = sectionHtml;

          const el = tmpl.content.querySelector('recommendations-add-to-cart') ||
                   tmpl.content.querySelector('product-recommendations');

          if (el && el.hasChildNodes()) {
            this.innerHTML = el.innerHTML;
            this.init();

            // Check if we need to fetch more recommendations
            const recommendationItems = this.querySelectorAll('.js-recommendation-item');
            const desiredCount = parseInt(this.dataset.limit || 4, 10);

            if (recommendationItems.length < desiredCount) {
              // Try to fetch more recommendations with a larger limit
              const newUrl = url.replace(/limit=\d+/, `limit=${desiredCount * 5}`);
              if (newUrl !== url) {
                const newDataset = { ...this.dataset, url: newUrl };
                this.dataset = newDataset;
                setTimeout(() => this.fetchRecommendations(), 500);
              }
            }

            return;
          }
        }
      } catch (e) {
        // Not JSON, continue with normal HTML parsing
      }

      const tmpl = document.createElement('template');
      tmpl.innerHTML = responseText;

      // Look for both old and new component styles for compatibility
      const el = tmpl.content.querySelector('recommendations-add-to-cart') ||
                tmpl.content.querySelector('product-recommendations');

      if (!el) {
        this.setErrorMsgState(theme.strings.noRecommendationsFound || 'No recommendations found');
        this.dispatchEvent(new CustomEvent('on:recommendations:error', {
          bubbles: true,
          detail: { error: 'No recommendations found' }
        }));
        return;
      }

      if (el && el.hasChildNodes()) {
        this.innerHTML = el.innerHTML;
        this.init();
      } else {
        this.setErrorMsgState(theme.strings.noRecommendationsAvailable || 'No recommendations available');
        this.dispatchEvent(new CustomEvent('on:recommendations:error', {
          bubbles: true,
          detail: { error: 'No recommendations available' }
        }));
      }
    } catch (error) {
      this.setErrorMsgState(theme.strings.errorLoadingRecommendations || 'Error loading recommendations');
      this.dispatchEvent(new CustomEvent('on:recommendations:error', {
        bubbles: true,
        detail: { error: error.message }
      }));
    }
  }

  init() {
    this.submitBtn = this.querySelector('.js-recommendations-add-to-cart');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.variantSelectors = this.querySelectorAll('.js-rec-variant-selector');
    this.itemTotal = this.querySelector('.js-recommendations-total');
    this.errorMsg = this.querySelector('.js-recommendations-error');
    this.buttonText = this.querySelector('.js-button-text');

    // Apply fallback translation if needed
    if (this.buttonText && this.buttonText.textContent.includes('Translation missing:')) {
      this.buttonText.textContent = window.theme?.strings?.addItemsToCart || 'Add items to cart';
    }

    // Make sure quantity inputs are properly initialized
    this.querySelectorAll('quantity-input').forEach(quantityInput => {
      if (!quantityInput.initialized && typeof window.QuantityInput === 'function') {
        Object.setPrototypeOf(quantityInput, window.QuantityInput.prototype);
        quantityInput.initialized = true;

        const input = quantityInput.querySelector('.qty-input__input');
        if (input) {
          quantityInput.input = input;
          quantityInput.currentQty = input.value;
          quantityInput.changeEvent = new Event('change', { bubbles: true });

          quantityInput.addEventListener('click', quantityInput.handleClick?.bind(quantityInput));
          input.addEventListener('focus', window.QuantityInput.handleFocus);
          input.addEventListener('keydown', quantityInput.handleKeydown?.bind(quantityInput));
        }
      }
    });

    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', this.handleSubmit.bind(this));
    }

    if (this.variantSelectors.length > 0) {
      this.variantSelectors.forEach(selector => {
        selector.addEventListener('change', this.updatePrice.bind(this));
      });
    }

    const quantityInputs = this.querySelectorAll('.qty-input__input');

    if (quantityInputs.length > 0) {
      quantityInputs.forEach(input => {
        input.addEventListener('change', this.updatePrice.bind(this));
      });
    }

    // Initial price calculation
    this.updatePrice();
  }

  updatePrice() {
    let total = 0;
    const items = this.querySelectorAll('.js-recommendation-item');

    items.forEach(item => {
      const variantSelector = item.querySelector('.js-rec-variant-selector');
      const quantityInput = item.querySelector('.qty-input__input');

      if (variantSelector && quantityInput) {
        let selectedOption;
        let price;

        if (variantSelector.tagName === 'SELECT') {
          selectedOption = variantSelector.options[variantSelector.selectedIndex];
          price = selectedOption && selectedOption.dataset.price ? parseFloat(selectedOption.dataset.price) : 0;
        } else {
          // Handle hidden input for single variant products
          price = variantSelector.dataset.price ? parseFloat(variantSelector.dataset.price) : 0;
        }

        const quantity = parseInt(quantityInput.value, 10) || 0;
        total += price * quantity;
      }
    });

    if (this.itemTotal) {
      // Find or create the money element
      let moneyEl = this.itemTotal.querySelector('.money');
      if (!moneyEl) {
        moneyEl = document.createElement('span');
        moneyEl.className = 'money';
        this.itemTotal.innerHTML = '';
        this.itemTotal.appendChild(moneyEl);
      }

      // Update the price amount - use money format WITHOUT currency code
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        // Use Shopify's built-in formatter with the standard money format (not money_with_currency)
        moneyEl.innerHTML = Shopify.formatMoney(total);
      } else {
        moneyEl.innerHTML = this.formatMoney(total);
      }

      // Set data attributes for currency converters
      moneyEl.setAttribute('data-price', total);

      // Trigger currency conversion if available
      if (typeof Currency !== 'undefined' && Currency.convertAll) {
        try {
          Currency.convertAll(Shopify.currency.active, Currency.currentCurrency);
        } catch (e) {
          // Silent fail for currency conversion errors
        }
      }
    }
  }

  formatMoneyForDisplay(cents) {
    // Try to use Shopify's currency format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }

    // Fallback to our own formatter
    return this.formatMoneyWithCurrency(cents);
  }

  async getShopifyFormattedPrice(cents) {
    try {
      // Create a hidden element with the initial formatted price to extract it
      if (!this._priceFormatterElement) {
        this._priceFormatterElement = document.createElement('div');
        this._priceFormatterElement.style.display = 'none';
        document.body.appendChild(this._priceFormatterElement);
      }

      // Use Shopify's money formatting via Liquid and extract the result
      this._priceFormatterElement.innerHTML = `<span class="money" data-price="${cents}">${cents}</span>`;

      // Wait for any currency converters to process the element
      await new Promise(resolve => setTimeout(resolve, 10));

      // Get the formatted price
      const moneyEl = this._priceFormatterElement.querySelector('.money');
      return moneyEl.innerHTML;
    } catch (error) {
      console.error('Error formatting price with Shopify:', error);
      throw error;
    }
  }

  formatMoneyWithCurrency(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    // Get money format and settings
    const moneyFormat = window.theme?.moneyFormat || '{{ amount }}';
    const showCurrency = window.theme?.settings?.show_currency_code || false;
    const useSuperscriptDecimals = window.theme?.settings?.superscript_decimals || false;

    // Format the price using the standard money formatter
    let formattedMoney = this.formatMoney(cents);

    // Handle superscript decimals if enabled
    if (useSuperscriptDecimals) {
      // Check if money format contains 'money' or '.' which would indicate it can't use superscript
      if (!moneyFormat.includes('money') && !moneyFormat.includes('.')) {
        // Determine decimal separator
        let separator = '.';
        if (moneyFormat.includes('comma_separator')) {
          separator = ',';
        }

        // Add superscript formatting
        formattedMoney = formattedMoney.split(separator).join('<sup>');

        // If showing currency code, add the closing tag before the currency
        if (showCurrency && window.theme?.currency?.code) {
          formattedMoney += '</sup> ' + window.theme.currency.code;
        } else {
          formattedMoney += '</sup>';
        }
      } else if (showCurrency && window.theme?.currency?.code) {
        // If we can't use superscript but need to show currency
        formattedMoney += ' ' + window.theme.currency.code;
      }
    } else if (showCurrency && window.theme?.currency?.code) {
      // No superscript, just add currency if needed
      formattedMoney += ' ' + window.theme.currency.code;
    }

    return formattedMoney;
  }

  formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const moneyFormat = window.theme?.moneyFormat || '{{ amount }}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number == null) return 0;

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsAmount = parts[1] ? (decimal + parts[1]) : '';

      return dollarsAmount + centsAmount;
    }

    let value = '';
    const match = moneyFormat.match(placeholderRegex);

    if (match) {
      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_with_space_separator':
          value = formatWithDelimiters(cents, 2, ' ', '.');
          break;
      }
    }

    return moneyFormat.replace(placeholderRegex, value);
  }

  async handleSubmit(evt) {
    evt.preventDefault();

    if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

    // Disable "Add to Cart" button until submission is complete
    this.submitBtn.setAttribute('aria-disabled', 'true');
    this.submitBtn.classList.add('is-loading');

    // Reset error state
    this.setErrorMsgState();

    try {
      const items = [];
      const recommendationItems = this.querySelectorAll('.js-recommendation-item');

      // Collect all items to add to cart
      recommendationItems.forEach(item => {
        const variantSelector = item.querySelector('.js-rec-variant-selector');
        const quantityInput = item.querySelector('.qty-input__input');

        if (variantSelector && quantityInput) {
          const variantId = parseInt(variantSelector.value, 10);
          const quantity = parseInt(quantityInput.value, 10) || 0;

          if (variantId && quantity > 0) {
            items.push({
              id: variantId,
              quantity: quantity
            });
          }
        }
      });

      if (items.length === 0) {
        this.setErrorMsgState(theme.strings.selectQuantityError || 'Please select a quantity for at least one product');
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: theme.strings.selectQuantityError || 'Please select a quantity for at least one product'
          }
        }));
        return;
      }

      // Get current cart state using fetch
      const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
      const oldCartData = oldCartResponse.ok ? await oldCartResponse.json() : null;

      // Prepare sections for cart drawer update
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      // Create form data for submission - matching Shopify's expected format exactly
      const formData = new FormData();
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
      });
      formData.append('sections', sections);
      formData.append('sections_url', window.location.pathname);

      // Add items to cart using the same format as product-form.js
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      }).catch(() => {
        // Catch network errors without console logs
        return { ok: false, status: 'network-error' };
      });

      // Parse the JSON response
      let data;
      try {
        data = await response.json();
      } catch (e) {
        // Handle JSON parsing errors silently
        this.setErrorMsgState('Error processing response');
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: 'Error processing response' }
        }));

        if (this.cartDrawer) {
          this.cartDrawer.refresh(true);
        }

        return;
      }

      // Handle any errors returned from the server (including 422)
      if (!response.ok || data.status) {
        let errorMessage = 'An error occurred adding items to cart';

        if (data) {
          if (typeof data.description === 'string') {
            errorMessage = data.description;
          } else if (typeof data.message === 'string') {
            errorMessage = data.message;
          }

          if (data.errors && typeof data.errors === 'object') {
            errorMessage = Object.entries(data.errors).map((item) => item[1].join(', '));
          }
        }

        this.setErrorMsgState(errorMessage);
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: errorMessage }
        }));

        // For inventory errors, still refresh the cart drawer
        if (this.cartDrawer) {
          this.cartDrawer.refresh(true);
        }

        return;
      }

      // Update cart icon
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && data.sections && data.sections['cart-icon-bubble']) {
        cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
      }

      // Update cart drawer if it exists - using proper accessibility practices
      if (this.cartDrawer) {
        // Instead of directly manipulating the drawer, dispatch an event to open it
        // This ensures the drawer component handles focus properly
        this.cartDrawer.renderContents(data, false);

        // Use the proper event to open the drawer
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
          detail: {
            opener: this.submitBtn
          }
        }));
      } else if (theme.settings.afterAtc === 'page') {
        window.location.href = theme.routes.cart;
      }

      // Get new cart state
      const newCartResponse = await fetch(`${theme.routes.cart}.js`).catch(() => {
        // Silently handle network errors for cart update
        return { ok: false };
      });

      const newCartData = newCartResponse.ok ? await newCartResponse.json() : null;

      // Reset quantities after successful add
      this.querySelectorAll('.qty-input__input').forEach(input => {
        input.value = '0';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      // Update total price
      this.updatePrice();

      // Success state
      this.submitBtn.classList.add('is-success');

      // Dispatch success event
      this.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: {
          cart: newCartData,
          items: items
        }
      }));

    } catch (error) {
      // Handle any unexpected errors
      this.setErrorMsgState('An unexpected error occurred');
      this.dispatchEvent(new CustomEvent('on:cart:error', {
        bubbles: true,
        detail: { error: 'An unexpected error occurred' }
      }));

      // Refresh cart drawer if it exists
      if (this.cartDrawer) this.cartDrawer.refresh(true);
    } finally {
      // Re-enable button and reset states after a delay
      setTimeout(() => {
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.classList.remove('is-success');
      }, 1400);
    }
  }

  /**
   * Handles error message display
   */
  setErrorMsgState(error = false) {
    if (!this.errorMsg) this.errorMsg = this.querySelector('.js-recommendations-error');
    if (!this.errorMsg) return;

    this.errorMsg.hidden = !error;
    if (error) {
      this.errorMsg.innerHTML = '';
      const errorArray = Array.isArray(error) ? error : [error];
      errorArray.forEach((err, index) => {
        if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
        this.errorMsg.insertAdjacentText('beforeend', err);
      });
    }
  }
}

customElements.define('recommendations-add-to-cart', RecommendationsAddToCart);
